"use client";

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { CheckIcon, Loader } from "lucide-react";
import { useState } from "react";
import { useClickOutside } from "@mantine/hooks";
import * as motion from "framer-motion/client";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md hover:cursor-pointer text-sm font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        outlinedark: "bg-gray-800 hover:bg-gray-700 text-white",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        subtle: "hover:bg-accent hover:text-accent-foreground",
        cta: "bg-orange-500 text-primary-foreground hover:bg-orange-500/90",
        warning: "bg-yellow-500 text-primary-foreground hover:bg-yellow-500/90",
        success: "bg-green-600 text-primary-foreground hover:bg-green-600/90",
        glow: "bg-orange-600 text-white hover:bg-orange-700",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        xl: "h-14 rounded-md px-10",
        icon: "h-10 w-10",
      },
      radius: {
        none: "rounded-none",
        default: "rounded-md",
        full: "rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  left?: React.ReactNode;
  right?: React.ReactNode;
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      radius,
      disabled,
      isLoading,
      left,
      right,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(
          "flex flex-row gap-4",
          buttonVariants({ variant, size, className, radius }),
        )}
        ref={ref}
        disabled={disabled || isLoading}
        {...props}
      >
        {left && <div className="self-center">{left}</div>}
        {children}
        {!isLoading && right && <div className="self-center">{right}</div>}
        {isLoading && (
          <div className="self-center">
            <Loader size={18} className="animate-spin" />
          </div>
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export const ConfirmDestructiveButton = ({
  children,
  variant,
  left,
  right,
  confirmationText = "Nochmal tippen zum Entfernen",
  isLoading,
  radius,
  size,
  className,
  disabled,
  onConfirm,
  ...props
}: ButtonProps & {
  confirmationText?: React.ReactNode;
  onConfirm?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const actualVariant = isConfirming ? "destructive" : variant;

  const actualLeft = isConfirming ? (
    <motion.div layout>
      <CheckIcon />
    </motion.div>
  ) : (
    left
  );
  const actualRight = isConfirming ? null : right;

  const ref = useClickOutside(() => setIsConfirming(false));

  return (
    <motion.button
      className={cn(
        "flex flex-row gap-4",
        buttonVariants({ variant: actualVariant, size, className, radius }),
      )}
      ref={ref}
      disabled={disabled || isLoading}
      data-confirming={isConfirming}
      initial={{
        borderRadius: "12px",
      }}
      animate={{
        borderRadius: isConfirming ? "8px" : "12px",
      }}
      onClick={(e) => {
        if (isConfirming) {
          setIsConfirming(false);
          onConfirm?.(e);
        } else {
          setIsConfirming(true);
        }
        props.onClick?.(e);
      }}
      layout
      {...(props as any)}
    >
      <motion.div layout className="flex flex-row gap-2">
        {actualLeft && <div className="self-center">{actualLeft}</div>}
        {isConfirming ? confirmationText : children}
        {!isLoading && actualRight && (
          <div className="self-center">{actualRight}</div>
        )}
        {isLoading && (
          <div className="self-center">
            <Loader size={18} className="animate-spin" />
          </div>
        )}
      </motion.div>
    </motion.button>
  );
};

export { Button, buttonVariants };
